import { useState, MouseEvent, useEffect } from "react";
import { Box, useTheme, Grid, Popover, Stack } from "@mui/material";
import BaseIconButton from "base/components/BaseIconButton";
import { Language, MenuOpen } from "@mui/icons-material";
import UserInformation from "base/components/UserInformation";
import Loader from "base/components/Loader";
import { useRecoilValue } from "recoil";
import { configState } from "base/store/config";
import { checkIsLoadedInIframe, getGroupwareUrl } from "base/utils";
import { TOP_NAV_HEIGHT } from "base/constant/ui";
import { LanguageResource } from "base/lib/i18n";
import { changeLanguage } from "i18next";
import { useUpdateConfig } from "base/hooks/useConfig";
import useSnackbar from "base/hooks/useSnackbar";
import UserPopupContent from "./UserPopupContent";
import LanguagePopupContent from "./LanguagePopupContent";
import useBreakpoint from "base/hooks/useBreakpoint";
import { sidebarState } from "base/store/sidebar";
import { useRecoilState } from "recoil";

const isLoadedInIframe = checkIsLoadedInIframe();
const TopNav = () => {
  const theme = useTheme();
  const { showSuccess } = useSnackbar();
  const { mutateAsync: updateConfigAsync, isPending } = useUpdateConfig();
  const { isGreaterThanMd } = useBreakpoint();

  const [sidebarData, setSidebarData] = useRecoilState(sidebarState);
  const { user, isLoadingConfig } = useRecoilValue(configState);
  const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLDivElement>(
    null
  );
  const [languageAnchorEl, setLanguageAnchorEl] =
    useState<HTMLButtonElement | null>(null);

  const onClickUserArea = (event: MouseEvent<HTMLDivElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const onCloseProfilePopup = () => {
    setProfileAnchorEl(null);
  };

  const onCloseLanguagePopup = () => {
    setLanguageAnchorEl(null);
  };

  const onClickOpenProfileGW = () => {
    window.open(`${getGroupwareUrl()}/app/#/profile/user_profile/`);
    onCloseProfilePopup();
  };

  const onChangeLanguage = (lang: LanguageResource) => {
    updateConfigAsync({
      _sidebar_data: {
        template_language: lang,
      },
    }).then((res) => {
      if (res.success) {
        changeLanguage(lang);
        showSuccess(res.msg);
        onCloseLanguagePopup();
      }
    });
  };

  const isOpenProfilePopup = Boolean(profileAnchorEl);
  const profilePopupID = isOpenProfilePopup ? "profile-popup" : undefined;
  const isOpenLanguagePopup = Boolean(languageAnchorEl);
  const languagePopupID = isOpenLanguagePopup ? "language-popup" : undefined;

  return (
    <>
      <Box
        sx={{
          background: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          pl: 1.5,
          pr: 2,
          height: TOP_NAV_HEIGHT,
        }}
      >
        <Grid container sx={{ height: "100%" }}>
          <Grid item xs={6} container alignItems={"center"}>
            {!isGreaterThanMd && (
              <BaseIconButton
                onClick={() => {
                  setSidebarData({
                    isShowSidebar: true,
                  });
                }}
              >
                <MenuOpen />
              </BaseIconButton>
            )}
          </Grid>

          {!isLoadedInIframe && (
            <Grid
              item
              xs={6}
              container
              alignItems={"center"}
              justifyContent={"flex-end"}
            >
              <Stack direction={"row"} spacing={1} alignItems={"center"}>
                <BaseIconButton
                  onClick={(e) => {
                    setLanguageAnchorEl(e.currentTarget);
                  }}
                >
                  <Language />
                </BaseIconButton>
                {isLoadingConfig ? (
                  <Loader size="small" padding={0} />
                ) : (
                  <Box
                    sx={{ cursor: "pointer" }}
                    onClick={onClickUserArea}
                    aria-describedby={profilePopupID}
                  >
                    <UserInformation
                      mainInfo={user?.name}
                      subInfo={user?.email}
                      userInfo={{ cn: user?.cn, no: user?.no }}
                      avatarContainerSx={{ alignSelf: "center" }}
                    />
                  </Box>
                )}
              </Stack>
            </Grid>
          )}
        </Grid>
      </Box>

      <Popover
        id={profilePopupID}
        anchorEl={profileAnchorEl}
        open={isOpenProfilePopup}
        onClose={onCloseProfilePopup}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <UserPopupContent onClickOpenProfileGW={onClickOpenProfileGW} />
      </Popover>

      <Popover
        id={languagePopupID}
        anchorEl={languageAnchorEl}
        open={isOpenLanguagePopup}
        onClose={onCloseLanguagePopup}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <LanguagePopupContent
          isLoading={isPending}
          onChangeLanguage={onChangeLanguage}
        />
      </Popover>
    </>
  );
};

export default TopNav;
